import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StuckTicketsPanel = ({ onResolved }) => {
    const [data, setData] = useState({ count: 0, rows: [], today_ct: '' });
    const [loading, setLoading] = useState(true);
    const [pickedKey, setPickedKey] = useState(null);
    const [winningInput, setWinningInput] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchStuck = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/lottery/admin/stuck-tickets`, { withCredentials: true });
            setData(data || { count: 0, rows: [] });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load stuck tickets');
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchStuck(); }, [fetchStuck]);

    const submitDraw = async (row) => {
        const expected = row.pick_type;
        const nums = winningInput
            .split(/[^0-9]+/)
            .filter((s) => s !== '')
            .map((s) => parseInt(s, 10));
        if (nums.length !== expected || nums.some((n) => isNaN(n) || n < 0 || n > 9)) {
            toast.error(`Need exactly ${expected} digits 0-9 (got: ${nums.join(',')})`);
            return;
        }
        setSubmitting(true);
        try {
            const { data: out } = await axios.post(`${API}/lottery/admin/draw`, {
                state: row.state,
                pick_type: row.pick_type,
                winning_numbers: nums,
                draw_date: row.draw_date,
                drawing_time: row.drawing_time,
            }, { withCredentials: true });
            toast.success(`Settled ${out.winners}W / ${out.losers}L · paid $${(out.total_payout || 0).toFixed(2)}`);
            setPickedKey(null);
            setWinningInput('');
            await fetchStuck();
            if (onResolved) onResolved();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to enter draw');
        }
        setSubmitting(false);
    };

    if (!loading && data.count === 0) {
        return null;
    }

    return (
        <div
            className="card-dark p-5 mb-4 relative overflow-hidden"
            style={{
                background: 'linear-gradient(135deg, rgba(255,59,48,0.10), rgba(255,140,0,0.05))',
                border: '1px solid rgba(255,59,48,0.40)',
            }}
            data-testid="stuck-tickets-panel"
        >
            <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl bg-[#FF3B30]/15 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-[#FF3B30]" />
                </div>
                <div>
                    <h3 className="text-base font-bold text-white">
                        Stuck Lottery Tickets ({data.count})
                    </h3>
                    <p className="text-[11px] text-[#A0AAB2]">
                        Pending tickets whose draw date has passed but no result was recorded.
                        Enter the winning numbers from the official state lottery and the system will auto-settle them.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-6">
                    <Loader2 className="w-5 h-5 text-[#FF3B30] animate-spin" />
                </div>
            ) : (
                <div className="space-y-2">
                    {data.rows.map((r) => {
                        const key = `${r.state}-${r.pick_type}-${r.draw_date}-${r.drawing_time}`;
                        const isPicked = pickedKey === key;
                        return (
                            <div
                                key={key}
                                className="rounded-lg p-3 bg-[#0a0a0a] border border-white/5"
                                data-testid={`stuck-ticket-row-${key}`}
                            >
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40">
                                            {r.state} Pick {r.pick_type}
                                        </span>
                                        <span className="text-sm text-white font-medium">{r.draw_date}</span>
                                        <span className="text-[10px] text-[#A0AAB2] uppercase tracking-wider">{r.drawing_time}</span>
                                        <span className="text-xs text-[#FF8C00]">
                                            {r.ticket_count} ticket{r.ticket_count === 1 ? '' : 's'} · ${r.total_at_risk.toFixed(2)} at risk
                                        </span>
                                    </div>
                                    {!isPicked ? (
                                        <Button
                                            size="sm"
                                            onClick={() => { setPickedKey(key); setWinningInput(''); }}
                                            data-testid={`stuck-resolve-${key}`}
                                            className="bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold"
                                        >
                                            <Wrench className="w-3.5 h-3.5 mr-1" />
                                            Enter winning numbers
                                        </Button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <Input
                                                autoFocus
                                                value={winningInput}
                                                onChange={(e) => setWinningInput(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') submitDraw(r); }}
                                                placeholder={`e.g. ${Array(r.pick_type).fill('5').join('-')}`}
                                                className="h-8 w-32 bg-[#050505] border-white/10 text-white text-sm"
                                                data-testid={`stuck-input-${key}`}
                                            />
                                            <Button
                                                size="sm"
                                                onClick={() => submitDraw(r)}
                                                disabled={submitting}
                                                className="h-8 bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold"
                                                data-testid={`stuck-submit-${key}`}
                                            >
                                                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Settle'}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => { setPickedKey(null); setWinningInput(''); }}
                                                className="h-8 text-white/60 hover:bg-white/5 hover:text-white"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {isPicked && (
                                    <p className="text-[10px] text-[#A0AAB2] mt-2">
                                        Tickets in this draw: {r.tickets.slice(0, 6).map(t => `${t.user_name} (${t.numbers.join('')}, $${t.amount})`).join('  ·  ')}
                                        {r.tickets.length > 6 ? ` +${r.tickets.length - 6} more` : ''}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default StuckTicketsPanel;
